import { ChangeEvent, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../../constants/svgIcon';
import { Profile } from '../../../../models/UserStore';
import {
  AvatarNode,
  LoadingSpinner,
  SvgIcon,
  TextInput,
} from '../../../Molecules';
import { RecentLoginInfo } from '../../Auth';
import styles from './WEAForgotPasswordTemplate.module.css';

type TextInputProps = {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

type WEAForgotPasswordTemplateProps = {
  username: TextInputProps;
  profile: Profile | undefined | null;
  email: string;
  onClickSearch: () => void;
  onClickSendEmail: () => void;
  onClickReturn: () => void;
  isLoading: boolean;
  errorMessage?: string;
  recentLoginInfo?: RecentLoginInfo[];
};

export const WEAForgotPasswordTemplate = memo(
  ({
    username,
    profile,
    email,
    onClickSearch,
    onClickSendEmail,
    onClickReturn,
    isLoading,
    errorMessage,
    recentLoginInfo,
  }: WEAForgotPasswordTemplateProps) => {
    const { t } = useTranslation();
    return (
      <div className={styles.forgotPassword}>
        {!profile ? (
          <div className={styles.centeredContainer}>
            <p className={styles.titleText}>{t('Find Your Account')}</p>
            <TextInput
              value={username.value}
              onChange={username.onChange}
              placeholder={t('Email / Username')}
              style={{
                backgroundColor: '#f0f0f0',
                borderWidth: errorMessage ? '1px' : '0px',
                borderColor: 'red',
              }}
              errorMessage={errorMessage}
            />
            <button
              className={`${styles.textButton} ${styles.sendEmailButton}`}
              onClick={onClickSearch}
            >
              {t('Search')}
            </button>
            {recentLoginInfo ? (
              <>
                <p className={styles.name}>
                  {t('Accounts you have logged in on this device')}
                </p>
                <div className={styles.recentLogins}>
                  {recentLoginInfo.map((login, index) => (
                    <div
                      key={index}
                      className={styles.loginItems}
                      onClick={login.onClick}
                    >
                      <AvatarNode
                        avatar={{
                          ...login.icon,
                          userId: login.userId || '',
                          source:
                            login.source && login.source.publicUrls?.small
                              ? {
                                  uri: login.source.publicUrls.small,
                                }
                              : '',
                        }}
                        size={80}
                      />
                      <div className={styles.textInfo}>
                        <div className={styles.name}>{login.name}</div>
                        {login.email && <div>{login.email}</div>}
                      </div>
                      <div className={styles.iconContainer}>
                        <SvgIcon
                          className={styles.arrow}
                          name={SVGIconType.Arrow}
                          height={20}
                          width={20}
                          stroke="#808080"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : undefined}
          </div>
        ) : (
          <div className={styles.centeredContainer}>
            <div className={styles.titleText}>{t('Reset Password')}</div>
            <AvatarNode
              avatar={{
                ...profile.avatar,
                userId: profile.userId,
                source:
                  profile.profilePicture &&
                  profile.profilePicture.publicUrls?.small
                    ? {
                        uri: profile.profilePicture.publicUrls.small,
                      }
                    : '',
              }}
              size={120}
            />

            <div className={styles.username}>{profile.name}</div>

            <div>{t('Send code via email')}</div>

            <div className={styles.email}>{email}</div>

            <button
              className={`${styles.textButton} ${styles.sendEmailButton}`}
              onClick={onClickSendEmail}
            >
              {t('Send Email')}
            </button>

            <button
              className={`${styles.textButton} ${styles.notYouButton}`}
              onClick={onClickReturn}
            >
              {t('Not you?')}
            </button>
          </div>
        )}
        {isLoading && <LoadingSpinner loading={isLoading} />}
      </div>
    );
  },
);
